import type { HTMLAttributes } from "react";
import { cn } from "../../lib/cn.js";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: "none" | "sm" | "md" | "lg";
  /** Visual treatment for grouping related content. */
  variant?: "default" | "elevated" | "tinted" | "interactive";
}

const paddingClasses = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
};

const variantClasses = {
  default: "bg-surface-raised border-border-default",
  elevated: "bg-surface-raised border-border-subtle shadow-card",
  tinted: "bg-surface-tint border-accent-muted",
  interactive:
    "bg-surface-raised border-border-default shadow-card transition-[box-shadow,border-color,transform] hover:border-accent-muted hover:shadow-card-hover motion-reduce:hover:transform-none",
};

export function Card({ className, padding = "md", variant = "default", children, ...props }: CardProps) {
  return (
    <div
      className={cn(
        "rounded-[var(--card-radius)] border",
        variantClasses[variant],
        paddingClasses[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("mb-4 space-y-1", className)} {...props}>
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn("font-display text-lg font-semibold text-content-primary", className)}
      {...props}
    >
      {children}
    </h2>
  );
}

export function CardDescription({ className, children, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-sm text-content-secondary", className)} {...props}>
      {children}
    </p>
  );
}
